import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isSignupEmailTakenError } from '../../../util/errors';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

import { createAccountStepSchema, type AccountStepValues } from '../schemas';

interface UserTypeOption {
  userType: string;
  label: string;
}

interface AccountStepProps {
  defaultValues?: Partial<AccountStepValues>;
  onSubmit: (data: AccountStepValues) => void;
  signupError: any | null;
  inProgress: boolean;
  /** Available user types. Show selector when multiple and no preselected type. */
  userTypes?: UserTypeOption[];
  /** Currently selected user type (null = not yet selected). */
  selectedUserType?: string | null;
  /** Called when the user picks a type from the selector. */
  onUserTypeChange?: (userType: string) => void;
  /** When true, the user type is locked from the URL and cannot be changed. */
  userTypeLocked?: boolean;
}

const AccountStep: React.FC<AccountStepProps> = ({
  defaultValues,
  onSubmit,
  signupError,
  inProgress,
  userTypes = [],
  selectedUserType,
  onUserTypeChange,
  userTypeLocked = false,
}) => {
  const intl = useIntl();
  const schema = useMemo(() => createAccountStepSchema(intl), [intl]);

  const form = useForm<AccountStepValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: defaultValues?.email ?? '',
      password: defaultValues?.password ?? '',
      firstName: defaultValues?.firstName ?? '',
      lastName: defaultValues?.lastName ?? '',
    },
  });

  const showUserTypeSelector = userTypes.length > 1 && onUserTypeChange && !userTypeLocked;
  const needsUserType = showUserTypeSelector && !selectedUserType;

  // When type is locked from URL, find the label for display
  const lockedTypeLabel = userTypeLocked && selectedUserType
    ? userTypes.find(ut => ut.userType === selectedUserType)?.label || selectedUserType
    : null;

  const errorMessageId = signupError
    ? isSignupEmailTakenError(signupError)
      ? 'AuthenticationPage.signupFailedEmailAlreadyTaken'
      : 'AuthenticationPage.signupFailed'
    : null;

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          {lockedTypeLabel ? (
            <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              <FormattedMessage id="SignupForm.signingUpAs" values={{ type: lockedTypeLabel }} />
            </div>
          ) : showUserTypeSelector ? (
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">
                <FormattedMessage id="SignupForm.userTypeLabel" />
              </label>
              <Select
                value={selectedUserType || ''}
                onValueChange={onUserTypeChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder={intl.formatMessage({ id: 'SignupForm.userTypePlaceholder' })} />
                </SelectTrigger>
                <SelectContent>
                  {userTypes.map(ut => (
                    <SelectItem key={ut.userType} value={ut.userType}>
                      {ut.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage id="SignupForm.emailLabel" />
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder' })}
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage id="SignupForm.passwordLabel" />
                </FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' })}
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="SignupForm.firstNameLabel" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' })}
                      autoComplete="given-name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="SignupForm.lastNameLabel" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' })}
                      autoComplete="family-name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {errorMessageId ? (
            <Alert variant="destructive">
              <AlertDescription>
                <FormattedMessage id={errorMessageId} />
              </AlertDescription>
            </Alert>
          ) : null}

          <Button
            type="submit"
            className="w-full"
            disabled={inProgress || needsUserType}
          >
            {inProgress ? <Loader2 className="animate-spin" /> : null}
            <FormattedMessage id="SignupForm.signUp" />
          </Button>

          {needsUserType ? (
            <p className="text-center text-sm text-muted-foreground">
              <FormattedMessage id="SignupForm.userTypeRequired" />
            </p>
          ) : null}
        </form>
      </Form>
    </div>
  );
};

export default AccountStep;
